import ReduxModel from "../generics/ReduxModel";
import SagaRunnable from "../generics/SagaRunnable";
import ReduxStateModel from "../scheduler/ReduxStateModel";
import ComponentStateModel from "../generics/ComponentStateModel";
import AxiosProxy, {axiosInstance} from "../api/AxiosProxy";
import {all, put, takeLeading} from "redux-saga/effects";
import {tryCatchWrapper} from "../../saga/tryCatchWrapper";
import {contextPollUntil, contextSaga, isDisabledWrapper} from "../../saga/sagaFunctions";
import {actionCreator} from "../../utilities/helperFunctions";
import {details} from "../../utilities/constants";


class NetworkConfigurationModel extends ReduxModel {

    static nom = 'NetworkConfigurationModel';
    static actions = NetworkConfigurationModel.buildActions();
    static actionCreators = NetworkConfigurationModel.buildActionCreators(NetworkConfigurationModel.actions);
    static reducer = NetworkConfigurationModel.buildReducer(NetworkConfigurationModel.actions);

    static componentActionCreators = {
        ...NetworkConfigurationModel.buildComponentUpdateActionCreators(),
        ...NetworkConfigurationModel.buildComponentSetActiveActionCreators()
    };

    constructor(model) {
        super();
        this.forceUpdate(model);
    }

    static validateFormData(state) {
        return !!(state.proxyHost && state.proxyPort && parseInt(state.proxyPort) >= 0);
    }

    static buildActions(type) {
        return {
            SET_DETAILS: `SET_${type}_DETAILS`,
            SUBMIT_FORM: `SUBMIT_${type}_FORM`,

            START_POLLING_DETAILS: `START_POLLING_${type}_DETAILS`,
            STOP_POLLING_DETAILS: `STOP_POLLING_${type}_DETAILS`,
            QUERY_DETAILS: `QUERY_${type}_DETAILS`
        };
    }

    static buildActionCreators(actions) {
        return {
            setDetails: actionCreator(actions.SET_DETAILS, 'configuration'),
            submitForm: actionCreator(actions.SUBMIT_FORM, 'formData'),

            startPollingDetails: actionCreator(actions.START_POLLING_DETAILS, 'period'),
            stopPollingDetails: actionCreator(actions.STOP_POLLING_DETAILS),
            queryDetails: actionCreator(actions.QUERY_DETAILS),
        };
    }

    static buildComponentUpdateActionCreators() {
        const components = [
            {
                key: 'networkConfigurationDisplay',
                type: 'Display',
                state: {
                    isFormActive: false
                }
            },
            {
                key: 'networkConfigurationForm',
                type: 'Form',
                state: {
                    isDisabled: false
                }
            }
        ];

        return ComponentStateModel.buildUpdateActionCreators(...components);
    }


    static buildComponentSetActiveActionCreators() {
        const components = [
            {
                key: 'NETWORK_CONFIGURATION_DISPLAY',
                type: 'Display'
            }
        ];

        return ComponentStateModel.buildSetActiveActionCreators(...components);
    }

    static buildReducer(actions) {
        return function (state = {}, action) {
            switch (action.type) {
                case actions.SET_DETAILS: {
                    const {configuration} = action.payload;

                    this.lastUpdated = Date.now();
                    return new this(configuration);
                }
                default: {
                    return state;
                }
            }
        }.bind(this);
    }
}

export class NetworkConfigurationApi {

    static get() {
        return axiosInstance.get('/scheduler/resources/networkConfiguration');
    }

    static put(updates) {
        return axiosInstance.put('/scheduler/resources/networkConfiguration', updates);
    }

    static clear() {
        return axiosInstance.del('/scheduler/resources/networkConfiguration');
    }
}

export class NetworkConfigurationSaga extends SagaRunnable {

    static activationComponent = 'NETWORK_CONFIGURATION_DISPLAY';

    static buildActivationEffects(dispatch) {
        return [
            put(NetworkConfigurationModel.actionCreators.startPollingDetails()),
            takeLeading(NetworkConfigurationModel.actions.SUBMIT_FORM, tryCatchWrapper, isDisabledWrapper, NetworkConfigurationModel.componentActionCreators.updateForm, contextSaga(this, 'submitForm')),
        ]
    }

    static buildDeactivationEffects() {
        return [
            put(NetworkConfigurationModel.actionCreators.stopPollingDetails())
        ]
    }

    static* submitForm(action) {
        const {formData} = action.payload;
        const {data} = yield NetworkConfigurationApi.put(formData);

        yield all([
            put(NetworkConfigurationModel.actionCreators.setDetails(data)),
            put(NetworkConfigurationModel.componentActionCreators.updateDisplay({isFormActive: false}))
        ]);
    }

    static* pollDetails(action) {
        const {period} = action.payload;
        yield contextPollUntil(NetworkConfigurationModel.actions.STOP_POLLING_DETAILS, period, this, 'queryDetails');
    }

    static* queryDetails() {
        const res = yield NetworkConfigurationApi.get();
        const key = details.NETWORK_CONFIGURATION;

        if (AxiosProxy.shouldUpdate(key, res)) {
            yield all([
                put(NetworkConfigurationModel.actionCreators.setDetails(res.data)),
                put(ReduxStateModel.actionCreators.setHasLoaded(key))
            ]);
        }
    }
}

export default NetworkConfigurationModel;
